<template>
  <div
    class="player"
    :class="playerClassName"
    :style="themeStyles"
    v-el:container
  >
    <modal
      v-if="isClassic() && searchIsVisible"
      centered
      hide-close-button
      background-color="rgba(0, 0, 0, 0.5)"
      @close="searchIsVisible = false"
    >
      <template slot="body">
        <search
          :search-term="searchTerm"
          :handle-on-press-search="handleOnPressSearch"
          :theme="configs.theme"
          v-ref:search
        ></search>
      </template>
    </modal>
    <hash-tag-modal
      v-if="
        isClassic() &&
        configs.preferences.enableHashtags &&
        podcast.categories.length
      "
      :is-visible="hashTagModalIsVisible"
      :on-press-close="handleOnPressShowMore"
      :theme="configs.theme"
      :tags="podcast.categories"
    ></hash-tag-modal>
    <div class="player__columns">
      <div
        v-if="
          (configs.preferences.enableAlbumArt && podcast.image.length) ||
          isMini()
        "
        class="player__column"
        :class="{
          'player__column--shrink': isMini(),
          'player__column--grow': isClassic(),
        }"
      >
        <div
          v-if="configs.preferences.enableAlbumArt && podcast.image.length"
          class="player__image-wrapper"
          :class="{ 'player__image-wrapper--mini': isMini() }"
        >
          <img
            :src="podcast.image"
            alt=""
            class="player__image"
            :class="{ 'player__image--mini': isMini() }"
          />
        </div>
        <play-pause
          v-if="isMini()"
          :is-playing="isPlaying"
          :is-loading="isLoading"
          :show-increments="false"
          :theme="configs.theme"
          @play="playMedia"
          @pause="pauseMedia"
          @decrement="decrement"
          @increment="increment"
        ></play-pause>
      </div>
      <div
        class="player__column"
        :class="{ 'player__column--grow': isClassic() }"
      >
        <div class="player__content">
          <player-info
            :is-classic="isClassic()"
            :title="podcast.name"
            :show-name="showTitle"
            :publish-date="podcast.timestampCreated"
            :is-playing="isPlaying"
            :url="podcast.shortUrl"
            :description="podcast.description"
            :theme="configs.theme"
            :show-options="shouldRenderAutoPlaySettings"
            :autoplay-active="hasAutoplay && autoplayActive"
            :is-live-stream="isLiveStream"
            @click-share="handleToggleShareOverlay"
            @toggle-autoplay="toggleAutoplay"
            v-ref:info
          ></player-info>
          <scrubber
            :duration="duration"
            :position="seekPos"
            :time-elapsed="timeElapsed"
            :time-remaining="timeRemaining"
            :enable-visualization="configs.preferences.enableVisualization"
            :show-time="
              configs.preferences.enableVisualization && isMini() ? false : true
            "
            :podcast-id="podcast.id"
            :theme="configs.theme"
            :is-live-stream="isLiveStream"
            v-ref:scrubber
            @seeking="onSeeking"
            @finished="onEnded"
            @can-play="handleScrubberCanPlay"
          ></scrubber>
          <play-pause
            v-if="isClassic()"
            :is-playing="isPlaying"
            :is-loading="isLoading"
            :show-increments="!isLiveStream"
            :theme="configs.theme"
            @play="playMedia"
            @pause="pauseMedia"
            @decrement="decrement"
            @increment="increment"
          ></play-pause>
          <hash-tag-list
            v-if="
              isClassic() &&
              configs.preferences.enableHashtags &&
              podcast.categories.length &&
              !isLiveStream
            "
            :handle-on-press-show-more="handleOnPressShowMore"
            :theme="configs.theme"
            :tags="podcast.categories"
            :character-limit="hashtagCharacterLimit"
            :disable-tags="!configs.preferences.enableGallery"
            @search="handleHashtagSearch"
          ></hash-tag-list>
          <div
            v-if="
              isClassic() &&
              configs.preferences.enableHashtags &&
              podcast.categories.length === 0
            "
            class="hash-tag-placeholder"
          ></div>
          <description
            v-if="isClassic() && configs.preferences.enableShowNotes"
            :description="podcast.description"
            :theme="configs.theme"
          ></description>
        </div>
      </div>
    </div>
    <div class="player__content">
      <gallery
        v-if="isClassic() && configs.preferences.enableGallery"
        :items="filteredPodcasts"
        :is-playing="isPlaying"
        :is-loading="isLoading"
        :is-classic="isClassic()"
        :podcast-id="podcast.id"
        :handle-search-click="handleSearchClick"
        :showing-filtered-podcasts="showingFilteredPodcasts"
        :handle-clear-search="handleClearSearch"
        :search-term="searchTerm"
        :theme="configs.theme"
        :show-title="showTitle"
        @load-podcast="loadPodcast"
        @pause="pauseMedia"
        v-ref:gallery
      ></gallery>
    </div>
    <audio
      v-el:player
      @timeupdate="onTimeUpdate"
      @canplay="onCanPlay"
      @ended="onEnded"
    >
      <source :src="audioSrc" />
      <p>Your browser doesn't support HTML5 audio.</p>
    </audio>
  </div>
</template>

<script>
import Scrubber from './Controls/Scrubber';
import PlayPause from './Controls/PlayPause';
import PlayerInfo from './PlayerInfo';
import HashTagList from './HashTag/HashTagList';
import HashTagModal from './HashTag/Modal';
import Description from './Description';
import Gallery from './Gallery/Gallery';
import { getLiveStreamUrl } from '../utils/utils';
import Search from './Search/Search.vue';
import Modal from './Utils/Modal';

// const urlPrefix = process.env.NODE_ENV === 'production' ? `//${location.host}` : '//post.newappsdev.futurimedia.com';
const urlPrefix = 'https://postv3.futurimedia.com'; // TODO: delete this line when pushing to production

export default {
  name: 'v3-player',
  components: {
    Scrubber,
    PlayPause,
    PlayerInfo,
    Description,
    HashTagList,
    HashTagModal,
    Gallery,
    Search,
    Modal,
  },
  props: {
    podcasts: Array,
    configs: Object,
    apiResourceUrl: String,
  },
  data() {
    return {
      podcast: this.podcasts[0],
      isPlaying: false,
      seekPos: 0,
      duration: 0,
      timeElapsed: 0,
      timeRemaining: 0,
      mediaReady: false,
      canPlayLiveStream: false,
      liveStreamUri: '',
      audioIsLoaded: false,
      hashTagModalIsVisible: false,
      searchIsVisible: false,
      searchTerm: '',
      showingFilteredPodcasts: false,
      isLoading: false,
      autoplayActive: true,
      dropToLive: false,
      isLiveStream: false,
      preloadedAudio: [], // podcast ids of audio files already loaded
      showTitle: '',
    };
  },
  computed: {
    playerClassName() {
      return [`player--${this.configs.mode}`];
    },

    audioSrc() {
      if (this.canPlayLiveStream) {
        return this.liveStreamUri;
      }

      return this.m4aURI;
    },

    m4aURI() {
      let uri = this.podcast.m4aFile;

      // Add the prefix if the uri doesn't already contain one
      // This was added when we switched to using //post.brian.devs.newappsdev.futurimedia.com
      // Probably should remove this later
      if (!uri.includes('http')) {
        uri = `${urlPrefix}${uri}`;
      }

      return uri;
    },

    shouldRenderAutoPlaySettings() {
      return ['next', 'live'].includes(this.configs.autoplay);
    },

    hasAutoplay() {
      const paramsHasAutoPlay = ['next', 'live'].includes(
        this.configs.autoplay
      );
      if (this.configs.autoplay === 'live') {
        this.dropToLive = true;
      }
      return paramsHasAutoPlay && this.autoplayActive;
    },

    podcastUrl() {
      const lstation = this.podcast.station
        ? this.podcast.station.toLowerCase()
        : '';
      return `${urlPrefix}/${lstation}`;
    },

    filteredPodcasts() {
      const searchTerm = this.searchTerm.toLowerCase().trim().replace('#', '');
      const limit = Number(this.configs.preferences.galleryLimit);

      if (!searchTerm) {
        return this.podcasts.slice(0, limit);
      }

      const filtered = [...this.podcasts].filter((podcast) => {
        const { name, categories, description } = podcast;

        // Matches name or description
        const matchesName = name.toLowerCase().includes(searchTerm);
        const matchesDescription = description
          .toLowerCase()
          .includes(searchTerm);
        if (matchesName || matchesDescription) {
          return true;
        }
        // Matches category
        let matchesCategory;
        for (let index = 0; index < categories.length; index++) {
          const category = categories[index];
          if (category.name.toLowerCase().includes(searchTerm)) {
            matchesCategory = true;
            break;
          }
        }

        return matchesCategory;
      });
      return filtered.slice(0, limit);
    },

    themeStyles() {
      return {
        background: this.configs.theme.background,
      };
    },

    ablumArtPlayerInfoComputedClass() {
      if (
        this.configs.preferences.enableAlbumArt &&
        this.podcast.image.length
      ) {
        return {
          wrapper: 'player__column-responsive',
          top: 'player__column-responsive__top',
          bot: 'player__column-responsive__bot',
        };
      }

      return {
        wrapper: 'player__column',
        top: '',
        bot: '',
      };
    },

    hashtagCharacterLimit() {
      const viewportWidth = document.body.offsetWidth;
      const isClassicWithAlbumArt =
        this.isClassic() && this.configs.preferences.enableAlbumArt;

      return isClassicWithAlbumArt && viewportWidth < 670 ? 40 : 90;
    },
  },
  methods: {
    playMedia() {
      if (
        !this.preloadedAudio.includes(this.podcast.id) ||
        (this.configs.preferences.enableVisualization &&
          this.preloadedAudio.length === 1)
      ) {
        this.isLoading = true;
      }

      if (this.configs.preferences.enableVisualization && !this.isLiveStream) {
        const scrubber = this.$refs.scrubber;
        scrubber.initWavesurfer(this.audioSrc);
        ['audioprocess', 'seek'].forEach((type) => {
          scrubber.wavesurfer.on(type, () => {
            this.onTimeUpdate();
          });
        });
        scrubber.wavesurfer.play();
      } else {
        if (this.canPlayLiveStream && !this.isPlaying) {
          // if live stream paused and play is clicked, original playlist should start again
          this.canPlayLiveStream = false;
        }

        if (!this.isPlaying) {
          const player = this.$els.player;
          const playPromise = player.play();

          // Backwards compatibility for autoplay
          if (playPromise === undefined) {
            playPromise
              .then(() => {
                // play back successful
              })
              .catch((error) => {
                console.error(error);
              });
          }
        }
      }
      this.isPlaying = true;
    },

    /**
     * Pause this media in particular.
     */
    pauseMedia() {
      if (this.configs.preferences.enableVisualization && !this.isLiveStream) {
        const scrubber = this.$refs.scrubber;
        if (scrubber.wavesurferInitialized) {
          scrubber.wavesurfer.pause();
          ['audioprocess', 'seek'].forEach((type) => {
            scrubber.wavesurfer.un(type, () => {
              this.onTimeUpdate();
            });
          });
        }
      } else {
        if (this.$els.player) {
          this.$els.player.pause();
        }
      }

      this.isPlaying = false;
    },

    /**
     * Updates the audio players if a new podcast ID is provided then plays the media
     *
     * @param {Number} id the podcast id
     * @return {void}
     */
    loadPodcast(id) {
      this.isLiveStream = false;
      this.pauseMedia();
      if (id && id !== this.podcast.id) {
        this.podcast = this.podcasts.find((podcast) => podcast.id === id);
        this.updatePlayer();
      }
      this.playMedia();
      this.$refs.info.scrollTitle();
    },

    updatePlayer() {
      if (this.configs.preferences.enableVisualization) {
        const scrubber = this.$refs.scrubber;
        scrubber.reset();
      } else {
        const audioPlayer = this.$els.player;
        audioPlayer.removeAttribute('src');

        if (
          !audioPlayer.src ||
          (!this.canPlayLiveStream &&
            audioPlayer.src &&
            !audioPlayer.src.includes('m4a'))
        ) {
          window.viblast(audioPlayer).stop(); // detatch viblast
          audioPlayer.setAttribute('src', this.audioSrc);
          audioPlayer.load();
        }
      }
    },

    playLiveStream() {
      this.isLiveStream = true;
      getLiveStreamUrl(this.podcast.station)
        .then((url) => {
          this.liveStreamUri = url;
          this.canPlayLiveStream = true;
          this.setupViblast();
          this.$els.player
            .play()
            // eslint-disable-next-line no-console
            .catch((err) => {
              console.warn('Play call interrupted', err);
            });
          this.isPlaying = true;
        })
        // eslint-disable-next-line no-console
        .catch((e) => {
          console.warn('Unable to fetch live stream', e);
        });
    },

    setupViblast() {
      try {
        const audioPlayer = this.$els.player;
        window.viblast(audioPlayer).stop(); // eslint-disable-line
        window.viblast(audioPlayer).setup({
          // eslint-disable-line
          key: 'ebb0b350-fb99-4e69-8337-0b18f080df4a',
          stream: this.audioSrc,
          forceHEAAC: true,
          autoplay: false,
        });
      } catch (e) {
        // This try/catch block is required. Viblast throws an error on iOS browsers
        //   that we ignore so this works.
      }
    },

    onTimeUpdate(event) {
      const scrubber = this.$refs.scrubber;
      const currentTime = this.configs.preferences.enableVisualization
        ? scrubber.wavesurfer.getCurrentTime()
        : parseInt(event.target.currentTime, 10);
      const duration = this.configs.preferences.enableVisualization
        ? scrubber.wavesurfer.getDuration()
        : parseInt(event.target.duration, 10);

      // Update the slider value to either new seek time or currentTime
      this.seekPos = (100 / (duration || 1)) * currentTime;
      this.timeElapsed = currentTime;
      this.timeRemaining = (duration || 0) - currentTime;
    },

    onCanPlay(event) {
      this.duration = event.target.duration;
      this.mediaReady = true;
      this.isLoading = false;
      this.preloadedAudio.push(this.podcast.id);
    },

    onEnded() {
      this.pauseMedia();

      if (this.dropToLive) {
        this.playLiveStream();
      } else if (this.hasAutoplay) {
        const currentPodIndex = this.filteredPodcasts.findIndex(
          (x) => x.id === this.podcast.id
        );
        const nextPod = this.filteredPodcasts[currentPodIndex + 1];

        if (nextPod && nextPod.id) {
          this.loadPodcast(nextPod.id);
          return;
        }
      }
    },

    /**
     * Event emitted when seeking through audio.
     * @param pos
     */
    onSeeking(pos) {
      const player = this.$els.player;
      const seekTime = player.duration * (pos / 100);
      player.currentTime = seekTime;
    },

    /**
     * Decrements the audio player
     *
     * @param {number} value the amount of seconds to decrement
     * @return {void}
     */
    decrement(value) {
      if (this.configs.preferences.enableVisualization) {
        const scrubber = this.$refs.scrubber;
        scrubber.wavesurfer.skipBackward(value);
      } else {
        // Reset to 0 if the decrement value is greater than the current time
        const player = this.$els.player;
        if (player.currentTime <= value) {
          player.currentTime = 0;
        } else {
          player.currentTime -= value;
        }
      }
    },

    /**
     * Increments the audio player
     *
     * @param {number} value the amount of seconds to increment
     * @return {void}
     */
    increment(value) {
      if (this.configs.preferences.enableVisualization) {
        const scrubber = this.$refs.scrubber;
        const duration = scrubber.wavesurfer.getDuration();
        const currentTime = scrubber.wavesurfer.getCurrentTime();
        if (duration - currentTime <= value) {
          this.pauseMedia();
        }
        scrubber.wavesurfer.skipForward(value);
      } else {
        // Set the currentTime to the duration if the episode would end in <= 30 seconds
        const player = this.$els.player;
        if (this.duration - this.currentTime <= value) {
          player.currentTime = this.duration;
        } else {
          player.currentTime += value;
        }
      }
    },

    handleOnPressShowMore() {
      this.hashTagModalIsVisible = !this.hashTagModalIsVisible;
    },

    isMini() {
      return this.configs.mode === 'mini';
    },

    isClassic() {
      return this.configs.mode === 'classic';
    },

    handleSearchClick() {
      this.searchIsVisible = true;
      this.$nextTick(() => {
        this.$refs.search.$els.searchInput.focus();
      });
    },

    handleOnPressSearch(_searchTerm) {
      if (_searchTerm.length > 0) {
        this.searchTerm = _searchTerm;
        this.searchIsVisible = false;
        this.showingFilteredPodcasts = true;
        this.$refs.gallery.currentPage = 0;
      }
    },

    handleClearSearch() {
      this.showingFilteredPodcasts = false;
      this.searchTerm = '';
    },

    handleOnChangeSearchTerm(e) {
      this.searchTerm = e;
    },

    handleToggleShareOverlay() {
      this.showShareOverlay = !this.showShareOverlay;
    },

    toggleAutoplay() {
      this.autoplayActive = !this.autoplayActive;
    },

    // used with Scrubber's Wave Surfer to ensure isLoading
    // is properly set to false
    handleScrubberCanPlay() {
      if (this.isLoading) {
        this.isLoading = false;
      }
      this.preloadedAudio.push(this.podcast.id);
    },

    retrieveShowData() {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', this.apiResourceUrl);
        // eslint-disable-next-line
        xhr.onreadystatechange = function () {
          if (this.readyState === 4) {
            if (this.status === 200) {
              resolve(JSON.parse(this.responseText));
            } else {
              reject(new Error(`${this.statusText} (${this.status})`));
            }
          }
        };
        xhr.send();
      });
    },

    handleHashtagSearch(e) {
      this.handleOnPressSearch(e);
    },
  },
  ready() {
    // Init the time remaining
    const duration = parseInt(this.podcast.duration, 10);
    this.timeRemaining = duration;
    this.retrieveShowData().then((res) => {
      this.showTitle = res.show_title;
    });
  },
};
</script>

<style lang="scss" scoped>
@import '../assets/_helpers';

$screen-sm: 540px;

.player {
  position: relative;
  border: 1px solid #dadae0;
  border-radius: 2px;
  margin-bottom: 1rem;
  display: flex;
  justify-content: center;
  align-items: stretch;
  width: 100%;
  overflow: hidden;
  &--mini {
    max-width: 320px;
    height: 75px;

    @media (min-width: 320px) {
      max-width: 571px;
      width: 100%;
    }

    .player__columns {
      width: 100%;
      flex-direction: row;
    }
  }

  &--classic {
    flex-direction: column;

    @media (min-width: $screen-sm) {
      padding: 1rem;
    }
    .player__content {
      padding: 0 1rem;
    }
  }

  &__columns {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: stretch;
    margin: 0 -0.5rem;
    @media (min-width: $screen-sm) {
      flex-direction: row;
      justify-content: flex-start;
      align-items: stretch;
    }
  }

  &__column {
    padding: 0 0.5rem;
    flex: 1 1 0;
    width: 100%;
    min-width: 0;
    &--shrink {
      flex: 0 0 75px;
      width: auto;
      position: relative;
      height: 75px;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    &--grow {
      min-width: 0;
      flex: 1 0 50%;
    }
  }

  &__image-wrapper {
    width: 100%;
    height: 0;
    padding-bottom: 100%;
    position: relative;
    margin-bottom: 1rem;
    &--mini {
      position: absolute;
      top: 4px;
      left: 4px;
      width: 67px;
      height: 65px;
      padding-bottom: 0;
    }
  }

  &__image {
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    object-fit: cover;
    object-position: 50%;
  }
}

.hash-tag-placeholder {
  height: 64px;
  margin-bottom: 8px;
  width: 100%;
}
</style>

<style>
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500&display=swap');

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}
</style>



// WEBPACK FOOTER //
// Player.vue?55335245